import { action, computed, observable } from "mobx";
import {
  CostCenterConfigItems,
  CreateCostCenter,
  UpdateCostCenter,
  DeleteCostCenter,
  RestoreCostCenter,
  ToggleCostCenterActive,
  CostCenterConfigItemFragment
} from "@graphql";

export class CostCenterSearchStore {
  @observable items: CostCenterConfigItemFragment[] = [];
  @observable loading = false;
  @observable textFilter = "";
  @observable organisationFilter = "";
  @observable showDeleted = false;

  constructor() {
    this.fetch();
  }

  @action
  fetch = async () => {
    this.loading = true;
    try {
      const result = await CostCenterConfigItems.func(
        {
          filter: {
            text: this.textFilter || undefined,
            organisation: this.organisationFilter || undefined,
            includeDeleted: this.showDeleted
          }
        },
        { fetchPolicy: "network-only" }
      );
      this.items = result.data.costCenterConfigItems;
    } finally {
      this.loading = false;
    }
  };

  @action
  setTextFilter = (value: string) => {
    this.textFilter = value;
    this.fetch();
  };

  @action
  setOrganisationFilter = (value: string) => {
    this.organisationFilter = value;
    this.fetch();
  };

  @action
  setShowDeleted = (value: boolean) => {
    this.showDeleted = value;
    this.fetch();
  };

  @action
  create = async (input: {
    costCenterId: string;
    name: string;
    organisation: string;
    isActive: boolean;
  }) => {
    await CreateCostCenter.func(
      { input },
      { refetchQueries: [CostCenterConfigItems.OperationName] }
    );
    await this.fetch();
  };

  @action
  update = async (
    id: string,
    input: {
      name?: string;
      organisation?: string;
      tenant?: string;
      isActive?: boolean;
    }
  ) => {
    await UpdateCostCenter.func(
      { id, input },
      { refetchQueries: [CostCenterConfigItems.OperationName] }
    );
    await this.fetch();
  };

  @action
  delete = async (id: string) => {
    await DeleteCostCenter.func(
      { id },
      { refetchQueries: [CostCenterConfigItems.OperationName] }
    );
    await this.fetch();
  };

  @action
  restore = async (id: string) => {
    await RestoreCostCenter.func(
      { id },
      { refetchQueries: [CostCenterConfigItems.OperationName] }
    );
    await this.fetch();
  };

  @action
  toggleActive = async (id: string) => {
    await ToggleCostCenterActive.func(
      { id },
      { refetchQueries: [CostCenterConfigItems.OperationName] }
    );
    await this.fetch();
  };

  @computed
  get sortedItems() {
    return [...this.items].sort((a, b) =>
      a.costCenterId.localeCompare(b.costCenterId)
    );
  }
}
